import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { FaExternalLinkAlt } from 'react-icons/fa';
import {
  KidStatus,
  KidTemplateAffectedShareClass,
} from '../../../../../api/kids/types';
import type {
  KidDocumentForm,
  PdfElement,
} from '../../../raidr/kidsV2/types/KidPdfGeneratorV2.types';
import {
  useGenerateKidDocument,
  useRemoveTemplateFromDocument,
} from '../../../../../api/kids/hooks/mutations';
import {
  useGetKidDataWithFigures,
  useGetKidMarkerAll,
} from '../../../../../api/kids/hooks/queries';
import { useGetClient } from '../../../../../api/client';
import DocumentPdfPreview from '../../../raidr/kidsV2/components/form/DocumentPdfPreview';
import { transformKidDataModelsToElements } from '../../../raidr/kidsV2/utils/kidDataTransformers';
import GenericModal from '../../../../ui/GenericModal';
import { btnDanger, btnSecondary } from '../templateManagement.constants';
import {
  formatKidTemplateFundId,
  formatKidTemplateFundName,
  formatKidTemplateShareClassName,
} from '../templateDocumentRows.utils';
import { AddTemplateToDocumentContext } from '../templateManagement.types';
import { buildKidEditorV2Url } from '../templateManagement.utils';
import TemplateDocumentMeta from './TemplateDocumentMeta';

const describeBlockType = (element: PdfElement): string => {
  if (element.template_id) return 'Template';
  if (!element.tag) return 'Block';
  return element.tag
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (character) => character.toUpperCase());
};

const stripHtml = (value: string): string => {
  const withoutTags = value.replace(/<[^>]*>/g, ' ');
  const decoded = withoutTags
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&#(\d+);/g, (_, code) => String.fromCharCode(Number(code)));
  return decoded
    .replace(/<[^>]*>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
};

const snippetFromContent = (content: PdfElement['content']): string => {
  if (content == null) return '';
  if (typeof content === 'number') return String(content);
  if (typeof content === 'string') return stripHtml(content);
  if (!Array.isArray(content) || content.length === 0) return '';

  if (typeof content[0] === 'string') {
    return (content as string[]).map(stripHtml).filter(Boolean).join(' · ');
  }

  if (Array.isArray(content[0])) {
    const firstRow = (content as string[][])[0] ?? [];
    return firstRow
      .map((cell) => stripHtml(String(cell ?? '')))
      .filter(Boolean)
      .join(' · ');
  }

  const nested = content[0] as { content?: PdfElement['content'] };
  if (nested && typeof nested === 'object' && 'content' in nested) {
    return snippetFromContent(nested.content);
  }

  return '';
};

const getBlockSnippet = (element: PdfElement): string => {
  if (element.template_id) {
    const templateName = element.template?.template_name?.trim();
    if (templateName) return templateName;
  }
  if (element.title?.trim()) return stripHtml(element.title);
  if (typeof element.rendered_content === 'string') {
    const rendered = stripHtml(element.rendered_content);
    if (rendered) return rendered;
  }
  return snippetFromContent(element.content);
};

const formatBlockSnippet = (
  element: PdfElement,
  resolveTokens: (value: unknown) => unknown,
): string => {
  const raw = getBlockSnippet(element);
  const resolved = resolveTokens(raw);
  return typeof resolved === 'string' ? stripHtml(resolved) : raw;
};

const RemoveTemplateFromDocumentModal: FC<
  AddTemplateToDocumentContext & {
    isOpen: boolean;
    onClose: () => void;
    document: KidTemplateAffectedShareClass;
  }
> = ({ isOpen, onClose, document, clientId, template }) => {
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);

  const removeTemplateFromDocument = useRemoveTemplateFromDocument(clientId);
  const generatePdf = useGenerateKidDocument();

  const { data: clientData } = useGetClient(clientId);
  const {
    data: kidData,
    isLoading,
    isError,
  } = useGetKidDataWithFigures(
    clientId,
    document.fund_id,
    document.share_class_id,
    document.document_language,
  );
  const { data: markers } = useGetKidMarkerAll(
    clientId,
    document.fund_id,
    document.share_class_id,
    document.kid_figure_calculation_date,
    document.last_updating_datetime,
  );

  const baseElements = useMemo<PdfElement[]>(
    () => (kidData ? transformKidDataModelsToElements(kidData as any[]) : []),
    [kidData],
  );

  const matchesTemplate = (element: PdfElement): boolean =>
    element.template_id === template.template_id ||
    element.template?.template_id === template.template_id;

  const removedElements = useMemo(
    () => baseElements.filter(matchesTemplate),
    [baseElements, template.template_id],
  );

  const previewElements = useMemo(
    () => baseElements.filter((element) => !matchesTemplate(element)),
    [baseElements, template.template_id],
  );

  const hasTemplateInLayout = removedElements.length > 0;

  const markerLookup = useMemo(() => {
    const lookup = new Map<string, string>();
    (markers || []).forEach((marker) => {
      lookup.set(`${marker.marker_type}:${marker.placeholder}`, marker.value);
    });
    return lookup;
  }, [markers]);

  const resolveMarkerTokens = useCallback(
    (value: unknown): unknown => {
      if (typeof value === 'string') {
        return value.replace(
          /\[\[([A-Z_]+):([a-z0-9_]+)\]\]/g,
          (match, markerType, placeholder) =>
            markerLookup.get(`${markerType}:${placeholder}`) || match,
        );
      }
      if (Array.isArray(value)) {
        return value.map((item) => resolveMarkerTokens(item));
      }
      return value;
    },
    [markerLookup],
  );

  const isDocumentLocked = document.status === KidStatus.EDITED;

  useEffect(() => {
    if (!isOpen || isLoading || isError) {
      return;
    }

    let cancelled = false;
    const run = async () => {
      try {
        const payload: KidDocumentForm = {
          client: clientData?.client_name_full || clientData?.client_name || '',
          fund_name: formatKidTemplateFundId(document),
          fund_name_full: formatKidTemplateFundName(document),
          share_class: formatKidTemplateShareClassName(document),
          date:
            document.kid_figure_calculation_date ||
            new Date().toISOString().split('T')[0],
          elements: previewElements.map((element) => ({
            ...element,
            content: resolveMarkerTokens(element.content) as PdfElement['content'],
          })),
        };

        const response = await generatePdf.mutateAsync(payload);
        if (cancelled) return;

        const blob = new Blob([response.data], { type: 'application/pdf' });
        const fileUrl = window.URL.createObjectURL(blob);

        setPdfUrl((previous) => {
          if (previous) window.URL.revokeObjectURL(previous);
          return fileUrl;
        });
      } catch (error) {
        if (!cancelled) {
          // eslint-disable-next-line no-console
          console.error('Failed to generate template preview PDF:', error);
        }
      }
    };

    run();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isOpen,
    isLoading,
    isError,
    previewElements,
    clientData,
    resolveMarkerTokens,
  ]);

  useEffect(
    () => () => {
      setPdfUrl((previous) => {
        if (previous) window.URL.revokeObjectURL(previous);
        return null;
      });
    },
    [],
  );

  const handleRemove = () => {
    if (isDocumentLocked) return;

    removeTemplateFromDocument.mutate(
      {
        fund_id: document.fund_id,
        share_class_id: document.share_class_id,
        template_id: template.template_id,
        document_language: document.document_language,
      },
      { onSuccess: () => onClose() },
    );
  };

  const isSaving = removeTemplateFromDocument.isPending;
  const isGenerating = generatePdf.isPending;

  return (
    <GenericModal
      isOpen={isOpen}
      setIsOpen={(open) => {
        if (!open) onClose();
      }}
      title="Remove template from document"
      className="max-w-6xl"
    >
      <div className="flex h-[72vh] min-h-0 flex-col">
        <TemplateDocumentMeta
          templateName={template.template_name}
          document={document}
        />

        {isDocumentLocked ? (
          <div className="mb-3 flex items-center gap-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2">
            <p className="min-w-0 flex-1 text-sm text-amber-950">
              This document is marked ready for review. Mark it as draft in the
              editor before changing the layout.
            </p>
            <Link
              to={buildKidEditorV2Url(document)}
              className="inline-flex shrink-0 items-center gap-1.5 rounded border border-amber-300 bg-white px-2.5 py-1.5 text-xs font-semibold text-amber-950 shadow-sm transition-colors hover:border-amber-400 hover:bg-amber-100"
              title="Open in editor"
            >
              <FaExternalLinkAlt className="h-2.5 w-2.5" aria-hidden />
              Go to Editor
            </Link>
          </div>
        ) : null}

        <div className="flex min-h-0 flex-1 gap-4">
          <div className="min-h-0 flex-1 overflow-hidden rounded-lg border border-gray-200 bg-gray-100">
            <DocumentPdfPreview
              previewUrl={pdfUrl}
              isPending={isLoading || isGenerating}
              isError={isError}
              title={null}
              className="h-full border-0 rounded-none"
              loadingMessage={
                isLoading ? 'Loading document…' : 'Generating preview…'
              }
              errorMessage="Could not load the document layout."
            />
          </div>

          <div className="flex w-72 shrink-0 flex-col rounded-lg border border-gray-200 bg-white">
            <div className="shrink-0 border-b border-gray-100 px-3 py-2.5">
              <h3 className="text-xs font-bold uppercase tracking-wide text-gray-600">
                Layout
              </h3>
              <p className="mt-0.5 text-[11px] text-gray-500">
                {hasTemplateInLayout
                  ? 'Preview shows the document without the highlighted block.'
                  : 'This template was not found in the document layout.'}
              </p>
            </div>
            <div className="min-h-0 flex-1 space-y-1 overflow-y-auto p-3">
              {baseElements.map((element, index) => {
                const snippet = formatBlockSnippet(element, resolveMarkerTokens);
                const isRemoving = matchesTemplate(element);
                return (
                  <div
                    key={`${element.fieldId}-${index}`}
                    className={
                      isRemoving
                        ? 'rounded-md border border-red-200 bg-red-50 px-2.5 py-1.5'
                        : 'rounded-md border border-gray-200 bg-gray-50/80 px-2.5 py-1.5'
                    }
                    style={
                      !isRemoving && element.template_id
                        ? { borderLeftWidth: 3, borderLeftColor: '#9ca3af' }
                        : undefined
                    }
                  >
                    <div className="flex items-start gap-2">
                      <span
                        className={
                          isRemoving
                            ? 'mt-0.5 w-4 shrink-0 text-[10px] font-bold tabular-nums text-red-400'
                            : 'mt-0.5 w-4 shrink-0 text-[10px] font-bold tabular-nums text-gray-400'
                        }
                      >
                        {index + 1}
                      </span>
                      <div className="min-w-0 flex-1">
                        <p
                          className={
                            isRemoving
                              ? 'text-[10px] font-semibold uppercase tracking-wide text-red-700'
                              : 'text-[10px] font-semibold uppercase tracking-wide text-gray-500'
                          }
                        >
                          {isRemoving ? 'Removing' : describeBlockType(element)}
                        </p>
                        {snippet ? (
                          <p
                            className="mt-0.5 line-clamp-2 text-xs font-medium leading-snug text-gray-800"
                            title={snippet}
                          >
                            {snippet}
                          </p>
                        ) : (
                          <p className="mt-0.5 text-xs italic text-gray-400">
                            No content
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
              {baseElements.length === 0 && !isLoading ? (
                <p className="px-1 py-2 text-[11px] text-gray-500">
                  This document has no blocks.
                </p>
              ) : null}
            </div>
          </div>
        </div>

        <div className="mt-4 flex shrink-0 items-center justify-between gap-3 border-t border-gray-100 pt-4">
          <p className="text-xs text-gray-500">
            This removes the template block from this document and increases its
            version number.
          </p>
          <div className="flex shrink-0 justify-end gap-2">
            <button
              type="button"
              className={btnSecondary}
              onClick={onClose}
              disabled={isSaving}
            >
              Cancel
            </button>
            <button
              type="button"
              className={btnDanger}
              onClick={handleRemove}
              disabled={isSaving || isError || isDocumentLocked}
            >
              {isSaving ? 'Removing…' : 'Remove from document'}
            </button>
          </div>
        </div>
      </div>
    </GenericModal>
  );
};

export default RemoveTemplateFromDocumentModal;
